import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import BackLink from '../../BackLink';
import EntityTitle from '../../EntityTitle';
import PersonIcon from '@mui/icons-material/Person';
import * as referenceSlice from '../../../data/reference.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as userSlice from '../../../data/user.slice';
import userUtils from '../../../services/userUtils';
import UserDialog from './UserDialog';
import BasicInfo from './BasicInfo';
import SecurityInfo from './SecurityInfo';
import TabPanel from '../../TabPanel';
import Entity, { entityClasses } from '../Entity';
import { useCanEditUser } from '../../../hooks/permissions/useCanEditUser';

export default function User() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');
  const canEditUser = useCanEditUser();

  const params = useParams<{ id: string }>();
  const [editorOpen, setEditorOpen] = React.useState(false);

  const loading = useAppSelector(userSlice.selectLoadingUser);
  const user = useAppSelector(userSlice.selectUser);
  const provider = useAppSelector(providerSlice.selectProvider);
  const userRoles = useAppSelector(userSlice.selectUserRoles);
  const loadingUserRoles = useAppSelector(userSlice.selectLoadingUserRoles);
  const userTypes = useAppSelector(referenceSlice.selectUserTypes);

  React.useEffect(() => {
    const id = parseInt(params.id!, 10);
    dispatch(userSlice.fetchUserAsync(id));
  }, [params, dispatch]);

  React.useEffect(() => {
    const typeId = userUtils.getTypeId(userTypes);
    if (typeId) {
      dispatch(userSlice.fetchUserRolesAsync(typeId));
    }
  }, [userTypes, dispatch]);

  React.useEffect(() => {
    if (user?.transportationProviderID) {
      dispatch(providerSlice.getProviderAsync(user.transportationProviderID));
    }
  }, [user, dispatch]);

  const editUser = () => {
    setEditorOpen(true);
  };

  const closeUserEditor = () => {
    setEditorOpen(false);
    dispatch(userSlice.resetSavingFlags());
  };

  return (
    <Entity>
      <BackLink />
      <Box component="div" className={entityClasses.content}>
        <Box component="div" className={entityClasses.left}>
          <Card className={entityClasses.card}>
            <CardHeader
              title={
                <EntityTitle
                  icon={<PersonIcon />}
                  label={`${user?.userFirstName} ${user?.userLastName}`}
                  loading={loading}
                  readOnly={!canEditUser}
                  onEdit={editUser}
                />
              }
            />
            {!loading && (
              <CardContent>
                <BasicInfo titleStyle={entityClasses.section} user={user} />
              </CardContent>
            )}
          </Card>
        </Box>
        {!loading && (
          <Box component="div" className={entityClasses.right}>
            <Paper className={entityClasses.tabs}>
              <Tabs
                indicatorColor="primary"
                textColor="primary"
                value={0}
                variant="fullWidth">
                <Tab label={t('user.security')} />
              </Tabs>
              <TabPanel value={0} index={0}>
                <SecurityInfo
                  titleStyle={entityClasses.section}
                  user={user}
                  loadingUserRoles={loadingUserRoles}
                  userRoles={userRoles}
                />
              </TabPanel>
            </Paper>
          </Box>
        )}
      </Box>
      {user && (
        <UserDialog
          open={editorOpen}
          providerId={provider?.transportationProviderID || 0}
          user={user}
          readOnly={false}
          close={closeUserEditor}
        />
      )}
    </Entity>
  );
}
